// Run state + generation availability.
//
// The two axes are rendered separately on purpose. `state` is a genuine Run
// Contract v1 lifecycle value and appears ONLY when a real Run exists;
// `availability` is the UI capability axis. Nothing here ever renders
// `blocked` or `unavailable` as if it were a Run lifecycle state, and
// nothing writes those words onto a Run object.
//
// When hosted generation is blocked this panel deliberately renders:
//   - a disabled Start button carrying the exact reason
//   - NO provider selector
//   - NO API-key field
//   - NO direct-provider fallback or "use local instead" affordance
// There is no code path in this file that could produce any of those, which
// is what the no-BYOK-fallback test asserts against the rendered output.

import { Button } from '@open-design/components';
import type { GiniHandoffRunView } from '@open-design/contracts/gini-platform';
import { useT } from '../../i18n';
import { CapabilityNotice } from './CapabilityNotice';
import styles from './HandoffLanding.module.css';

const BADGE_CLASS: Record<string, string> = {
  queued: styles.badgeQueued as string,
  running: styles.badgeRunning as string,
  waiting_for_user: styles.badgeWaiting as string,
  succeeded: styles.badgeSucceeded as string,
  failed: styles.badgeFailed as string,
  canceled: styles.badgeCanceled as string,
  expired: styles.badgeExpired as string,
};

interface Props {
  run: GiniHandoffRunView;
}

export function RunStatePanel({ run }: Props) {
  const t = useT();
  const canStart = run.availability.state === 'available';

  return (
    <section className={styles.card} aria-labelledby="gini-handoff-generation">
      <h2 className={styles.cardTitle} id="gini-handoff-generation">
        {t('giniHandoff.generationHeading')}
      </h2>

      <div className={styles.stateRow}>
        {run.state ? (
          <span className={`${styles.badge} ${BADGE_CLASS[run.state] ?? ''}`} data-testid="gini-handoff-run-state">
            {run.state}
          </span>
        ) : (
          <span
            className={`${styles.badge} ${
              run.availability.state === 'blocked' ? styles.badgeBlocked : styles.badgeUnavailable
            }`}
            data-testid="gini-handoff-run-availability"
          >
            {run.availability.state}
          </span>
        )}
        {run.runId ? <span className={styles.metaValue}>{run.runId}</span> : null}
      </div>

      <CapabilityNotice capability={run.availability} testId="gini-handoff-generation-notice" />

      <div className={styles.actions}>
        <Button type="button" variant="primary" disabled={!canStart} data-testid="gini-handoff-start-generation">
          {t('giniHandoff.startHostedGeneration')}
        </Button>
      </div>
    </section>
  );
}
